// @ts-strict-ignore
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { ArticleCreationManager } from '../manager/article-creation.manager';
import { SearchQueryBuilder } from '../../../util/search-query-builder';
import { ArrayUtils } from '@shared/util/array-utils';
import { FavoriteArticlesFilter } from './favorite-articles-filter';
import { GroupedCubesSearchRequirements } from '../../../model/vo/grouped-cubes-search-requirements';
import { AlternativeReplaceArticleManager } from '@project/components/article/manager/alternative-replace-article.manager';
import { AccessoryTypeFilter, AccessoryTypeFiltersBuilder } from '../model/accessory-type-filter';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { DefaultReplaceArticleManager } from '../manager/default-replace-article.manager';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AccessorySearchResultVO, ArticleInfoConnectionSetVO } from '../model/vo/accessory-search-result.vo';
import { map } from 'rxjs/operators';
import { SearchQueryType } from '../model/vo/search-query-type';
import { Observable } from 'rxjs';
import { AccessoryService, AccessoryViewData } from '@article/service/accessory.service';

export class AddArticlePresenter {
  private _articleSearchType: string = SearchQueryType.ACCESSORY_ARTICLE;
  private _isReplaceOperation: boolean = false;
  private _excludedArticleTypes: string[];

  public allAccessoryTypeFilters: AccessoryTypeFilter[] = [];
  private _accessoryTypeFilter: AccessoryTypeFilter;
  public showExcludedFromPrinter: boolean = false;
  private _loadedAccessories: ArticleInfoConnectionSetVO[] = [];

  constructor(
    private articleCreationManager: ArticleCreationManager,
    private favoriteArticlesFilter: FavoriteArticlesFilter,
    private searchQueryBuilder: SearchQueryBuilder,
    private alternativeReplaceArticleManager: AlternativeReplaceArticleManager,
    private defaultReplaceArticleManager: DefaultReplaceArticleManager,
    private localeResourceManager: LocaleResourceManager,
    private accessoryService: AccessoryService,
    initialFilter: AccessoryTypeFilter | undefined,
  ) {
    // Add message handlers here
    this.initAccessoryTypes();
    this.accessoryTypeFilter = initialFilter ?? this.allAccessoryTypeFilters[0];
  }

  public removeFromContext(): void {
    this.articleCreationManager.clean();
    this.articleCreationManager = null;
  }

  private initAccessoryTypes(): void {
    this.allAccessoryTypeFilters = new AccessoryTypeFiltersBuilder(this.localeResourceManager)
      .addExclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_TOP_RESULTS, [
        ArticleTypeConstants.TYPE_WIRE_BRIDGE,
        ])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_END_PLATE, [
          ArticleTypeConstants.TYPE_END_PLATE,
          ArticleTypeConstants.TYPE_MCS_GDS_END_PLATE
        ])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_BRIDGE, [ArticleTypeConstants.TYPE_BRIDGE])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_INSULATION_STOP, [ArticleTypeConstants.TYPE_INSULATION_STOP])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_PROTECTIVE_WARNING_MARKER, [ArticleTypeConstants.TYPE_PROTECTIVE_WARNING_MARKER])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_STRAIN_RELIEF, [ArticleTypeConstants.TYPE_STRAIN_RELIEF])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_LABELING, [ArticleTypeConstants.TYPE_LABELING])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_MARKER_CARRIER, [ArticleTypeConstants.TYPE_MARKER_CARRIER])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_LOCKING, [ArticleTypeConstants.TYPE_LOCKING])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_CODING_KEY, [ArticleTypeConstants.TYPE_CODING_KEY])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_WIRE_FERRULE, [ArticleTypeConstants.TYPE_WIRE_FERRULE])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_WIRE_BRIDGE, [ArticleTypeConstants.TYPE_WIRE_BRIDGE])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_MCS_CONNECTORS, [
          ArticleTypeConstants.TYPE_MULTI_CONNECTORS,
          ArticleTypeConstants.TYPE_MALE_HEADER_FOR_PCB
        ])
      .addInclusionFilter(AccessoryTypeFilter.LANGUAGE_KEY_OTHER_ACCESSORIES, [
          ArticleTypeConstants.TYPE_GENERAL_ACCESSORY,
          ArticleTypeConstants.TYPE_ELECTRONIC_ATTACHMENT,
          ArticleTypeConstants.TYPE_INSTALLATION_TECHNOLOGY,
          ArticleTypeConstants.TYPE_N_BUSBAR,
          ArticleTypeConstants.TYPE_COVER,
          ArticleTypeConstants.TYPE_BLOCKING_ZONE,
          ArticleTypeConstants.TYPE_CONNECTORS,
          ArticleTypeConstants.TYPE_RAIL_ACCESSORY,
          ArticleTypeConstants.TYPE_UNKNOWN,
        ])
      .build();

  }

  public async prepareArticle(suitableConnections: SuitableConnectionsVO): Promise<void> {
    if (!suitableConnections) {
      return;
    }
    switch (this._articleSearchType) {
      case SearchQueryType.CARRIER_CONNECTABLE_ALTERNATIVE_ARTICLE:
        await this.alternativeReplaceArticleManager.replaceSelectedArticles(suitableConnections.article);
        break;
      case SearchQueryType.ACCESSORY_ALTERNATIVE_ARTICLE:
        await this.alternativeReplaceArticleManager.replaceSelectedAccessoryArticles(this.articleCreationManager, suitableConnections,
          this.searchQueryBuilder.connectionCubeVectors);
        break;
      default:
        if (this._isReplaceOperation) {
          await this.defaultReplaceArticleManager.replaceSelectedAccessoryArticles(this.articleCreationManager, suitableConnections,
            this.searchQueryBuilder.connectionCubeVectors);
        } else {
          await this.articleCreationManager.createArticlesBySuitableConnections(suitableConnections, this.searchQueryBuilder.connectionCubeVectors);
        }
        break;
    }
  }

  public setDataToConnectArticle(accessoryViewData: AccessoryViewData): void {
    const groupedCubesSearchRequirements: GroupedCubesSearchRequirements[] = accessoryViewData.groupedCubesSearchRequirements;
    const searchByArticles: boolean = accessoryViewData.searchByArticles;
    const includedArticleTypes: string[] = accessoryViewData.includedArticleTypes;
    const excludedArticleTypes: string[] = accessoryViewData.excludedArticleTypes;
    const detachArticleUsageIds: string[] = accessoryViewData.detachArticleUsageIds || [];
    const replaceArticleUsageIds: string[] = accessoryViewData.replaceArticleUsageIds || [];
    const searchType: string = accessoryViewData.searchType;
    const searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled: boolean =
      accessoryViewData.searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled;

    this._excludedArticleTypes = excludedArticleTypes;
    this._articleSearchType = searchType;
    this._isReplaceOperation = accessoryViewData.replaceOperation;

    this.searchQueryBuilder.searchByArticles = searchByArticles;
    this.searchQueryBuilder.searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled =
      searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled;
    this.searchQueryBuilder.searchType = searchType;

    const includeMultipleCommonVector: boolean = SearchQueryType.ACCESSORY_ALTERNATIVE_ARTICLE !== searchType;
    this.searchQueryBuilder.prepareGroupedConnections(groupedCubesSearchRequirements, includeMultipleCommonVector);
    if (includedArticleTypes) {
      this.searchQueryBuilder.includedArticleTypes = includedArticleTypes;
    }

    this.articleCreationManager.detachArticleUsageIds = detachArticleUsageIds;
    this.articleCreationManager.replaceArticleUsageIds = replaceArticleUsageIds;
    const isSearchForAlternatives: boolean =
      searchType === SearchQueryType.ACCESSORY_ALTERNATIVE_ARTICLE ||
      searchType === SearchQueryType.CARRIER_CONNECTABLE_ALTERNATIVE_ARTICLE;
    if (isSearchForAlternatives || !ArrayUtils.isEmpty(detachArticleUsageIds)) {
      this.articleCreationManager.checkFreeConnection = false;
    }
  }

  public loadFavoriteTypeIds(): Observable<number[]> {
    return this.favoriteArticlesFilter.loadFavoriteTypeIds();
  }

  public get accessoryTypeFilter(): AccessoryTypeFilter {
    return this._accessoryTypeFilter;
  }

  public set accessoryTypeFilter(value: AccessoryTypeFilter) {
    this._accessoryTypeFilter = value;
  }

  public findAccessoryArticles(): Observable<ArticleInfoConnectionSetVO[]> {
    this._loadedAccessories = [];
    this.searchQueryBuilder.electedArticleIds = this.getFavoriteArticles();

    // Apply accessoryTypeFilter only for accessory search.
    this.searchQueryBuilder.includedArticleTypes = this.accessoryTypeFilter.includedArticleTypes;

    return this.loadArticlesByRequirements();
  }

  private getFavoriteArticles(): number[] {
    return this.favoriteArticlesFilter.getFavoriteArticleIds();
  }

  private loadArticlesByRequirements(): Observable<ArticleInfoConnectionSetVO[]> {
    return this.accessoryService
      .searchAccessoriesForBuilder(this.searchQueryBuilder, this.getExcludedArticleTypes(this.searchQueryBuilder.includedArticleTypes))
      .pipe(map(accessorySearchResult => this.handleSuccess_allArticlesLoaded(accessorySearchResult)));
  }

  private handleSuccess_allArticlesLoaded(accessorySearchResult: AccessorySearchResultVO): ArticleInfoConnectionSetVO[] {
    const accessories: ArticleInfoConnectionSetVO[] = accessorySearchResult.articleInfoConnectionSet;

    if (accessorySearchResult.queryElectedArticleIdsIncludedInCompleteSearchResult) {
      this.favoriteArticlesFilter.resultElectedArticleIds = accessorySearchResult.queryElectedArticleIdsIncludedInCompleteSearchResult;
    }
    this._loadedAccessories = accessories;
    const filteredAccessories = this.applyPrintLabelingFilter(accessories);

    this.searchQueryBuilder.electedArticleIds = null;
    return filteredAccessories;
  }

  private applyPrintLabelingFilter(accessories: ArticleInfoConnectionSetVO[]): ArticleInfoConnectionSetVO[] {
    const filteredAccessories: ArticleInfoConnectionSetVO[] = !this.showExcludedFromPrinter
      ? this.filterOutPrintLabelingAccessories(accessories)
      : accessories;
    return filteredAccessories;
  }

  private filterOutPrintLabelingAccessories(accessories: ArticleInfoConnectionSetVO[]): ArticleInfoConnectionSetVO[] {
    const newFilteredAccessories: ArticleInfoConnectionSetVO[] = [];
    for (const accessory of accessories) {
      if (!accessory.isExcludedFromPrintLabeling) {
        newFilteredAccessories.push(accessory);
      }
    }
    return newFilteredAccessories;
  }

  public revalidateFilters(): ArticleInfoConnectionSetVO[] {
    return this.applyPrintLabelingFilter(this._loadedAccessories);
  }

  private getExcludedArticleTypes(includedArticleTypes: string[]): string[] | undefined {
    let excludedArticleTypes: string[] | undefined = undefined;
    if (this._excludedArticleTypes && this._accessoryTypeFilter.excludedArticleTypes) {
      excludedArticleTypes = [...this._excludedArticleTypes, ...this._accessoryTypeFilter.excludedArticleTypes];
    }
    if (this._excludedArticleTypes) {
      excludedArticleTypes = this._excludedArticleTypes;
    }
    if (this._accessoryTypeFilter.excludedArticleTypes) {
      excludedArticleTypes = this._accessoryTypeFilter.excludedArticleTypes;
    }
    if (excludedArticleTypes && includedArticleTypes) {
      excludedArticleTypes = excludedArticleTypes.filter((value) => !includedArticleTypes.includes(value));
    }
    return excludedArticleTypes;
  }
}
